import React, { useState } from 'react';
import { CalendarIcon, DownloadIcon, ExternalLinkIcon, FileSpreadsheetIcon, FolderIcon, SearchIcon, SearchXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { FilterSelect } from '../components/ui/FilterSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { reports } from '../data/reports';
import { formatDate, relativeDay } from '../utils/format';
const FOLDERS = Array.from(new Set(reports.map(r => r.folder)));
export function Reports() {
  const [query, setQuery] = useState('');
  const [folder, setFolder] = useState('All');
  const q = query.trim().toLowerCase();
  const list = reports.filter(r => (folder === 'All' || r.folder === folder) && (!q || `${r.name} ${r.description} ${r.folder}`.toLowerCase().includes(q)));
  const clear = () => {
    setQuery('');
    setFolder('All');
  };
  return <div data-id="element-1184" data-mp="2fuq709levkk6">
      <PageHeader eyebrow="Reports" title="Standard reports" description="Saved HR reports. Open one to run it live, or export the latest snapshot." data-id="element-1185" data-mp="2fv9gr30wr2n4" />

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-1186" data-mp="2auc230f3wqqd">
        <div className="relative sm:w-72" data-id="element-1187" data-mp="25xbtoiusbbw3">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-1188" data-mp="25ydf9h9ffgs2" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search reports" aria-label="Search reports" className="h-10 w-full rounded-xl border-0 bg-white pl-10 pr-3 text-sm text-navy ring-1 ring-inset ring-slate-200 placeholder:text-mutedlight focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-1189" data-mp="2ylhgdb5efayk" />
        </div>
        <FilterSelect label="Folder" allLabel="All folders" value={folder} options={FOLDERS} onChange={setFolder} className="sm:w-52" data-id="element-1190" data-mp="3hd0htdjrygyh" />
        <span className="text-xs text-muted sm:ml-auto" data-id="element-1191" data-mp="1angrg5kuhzhm">
          {list.length} of {reports.length} reports
        </span>
      </div>

      <p className="mb-5 flex items-center gap-1.5 text-xs text-mutedlight" data-id="element-1192" data-mp="1fli7siqvzkpe">
        <ExternalLinkIcon className="h-3 w-3" aria-hidden="true" data-id="element-1193" data-mp="10y6xslacs8re" />
        These are standard Salesforce reports — “Open report” opens them in Salesforce in a new tab.
      </p>

      {list.length === 0 ? <EmptyState icon={SearchXIcon} title="No reports match" description="Try another search or folder." action={<Button size="sm" onClick={clear} data-id="element-1195" data-mp="wtpf6pfqc1i7">
              Clear filters
            </Button>} data-id="element-1194" data-mp="rwphp2wy472p" /> : <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-1196" data-mp="mxcjxn4jdflc">
          {list.map(r => <li key={r.id} className="flex flex-col gap-4 px-6 py-5 transition-colors hover:bg-canvas/60 lg:flex-row lg:items-center" data-id="element-1197" data-mp="2capsjojolps9">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-navy/[0.05] text-navy" data-id="element-1198" data-mp="5o0ka1gamh1n">
                <FileSpreadsheetIcon className="h-5 w-5" data-id="element-1199" data-mp="qi8m3aw5npy" />
              </span>
              <div className="min-w-0 flex-1" data-id="element-1200" data-mp="fevafgwnu0sd">
                <p className="text-[15px] font-semibold text-navy" data-id="element-1201" data-mp="ffo87sdn5fyd">{r.name}</p>
                <p className="mt-0.5 text-sm text-muted" data-id="element-1202" data-mp="amv2p2acm3l8">{r.description}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted" data-id="element-1203" data-mp="oe9y68lfzx96">
                  <span className="inline-flex items-center gap-1.5" data-id="element-1204" data-mp="ke7szpgi0lp5">
                    <FolderIcon className="h-3.5 w-3.5 text-azure" data-id="element-1205" data-mp="yd7lj874tkql" /> {r.folder}
                  </span>
                  <span className="inline-flex items-center gap-1.5" title={formatDate(r.lastRun)} data-id="element-1206" data-mp="tee3r3ktb7km">
                    <CalendarIcon className="h-3.5 w-3.5" data-id="element-1207" data-mp="304c6xmih1p2h" /> Last run {relativeDay(r.lastRun).toLowerCase()} · {formatDate(r.lastRun, 'MMM d')}
                  </span>
                  <span data-id="element-1208" data-mp="2v7lud95vlblt">Owner: {r.owner}</span>
                </div>
              </div>
              <div className="flex shrink-0 gap-2" data-id="element-1209" data-mp="2wmer29vymvqw">
                <Button icon={DownloadIcon} onClick={() => toast('Opens in Salesforce', {
            description: `${r.name} — export runs from the Salesforce report.`
          })} data-id="element-1210" data-mp="31l84k2b8ymr2">
                  Export
                </Button>
                <Button variant="primary" iconRight={ExternalLinkIcon} onClick={() => toast('Opens in Salesforce', {
            description: `${r.name} opens in a new tab.`
          })} data-id="element-1211" data-mp="3bk72apuh4xza">
                  Open report
                </Button>
              </div>
            </li>)}
        </ul>}
    </div>;
}